import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { sendLoyaltyModalEvent } from 'common/util/gtm-helper';
import { isEmpty } from 'underscore';
import Button from 'common/components/atoms/button';
import AlertIcon from 'common/icons/loyalty/alert';
import defaultStyles from 'commonStyles/components/coupon/_loyalty';
import styleOverrides from 'styles/components/coupon/_loyalty';
import useStyleOverrides from 'common/components/hooks/use-style-overrides';
import { translate } from 'common/localization';
import { Controller, useForm } from 'react-hook-form';
import { isValidEmail } from 'common/util/auth-modal-helper';
import AlertBox from './alert-box';
import { isEmployeeEmail } from 'common/util/loyalty/sign-up';

const ErrorBox = (props) => {
  const styles = useStyleOverrides(defaultStyles, styleOverrides);

  return (
    <div className={styles.alertBoxWrapper} role="alert">
      <div className={styles.alertBoxTextWrapper}>
        {props.title ? (
          <>
            <div className={styles.alertBoxContainer}>
              <AlertIcon className={styles.alertIconLoyalty} />
              <div className={styles.alertBoxTextWrapper}>
                <div className={styles.alertBoxTitle}>{props.title}</div>
                <span className={styles.alertBoxDescription}>{props.description}</span>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.alertBoxContainer}>
            <AlertIcon className={styles.alertIconLoyalty} />
            <span className={styles.alertBoxDescription}>{props.description}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const LoyaltyCouponNewUser = ({
  claimLoyaltyCouponByEmail,
  error,
  setError,
  disclaimers,
  onExitModal,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);
  const styles = useStyleOverrides(defaultStyles, styleOverrides);

  const {
    handleSubmit,
    control,
    setError: setFormError,
    formState,
  } = useForm();

  const { isSubmitting, errors } = formState;
  const toggleLoading = () => setIsLoading(prev => !prev);
  // This implementation is because formState.errors will not trigger the useEffect and formState triggers useEffect twice.
  const triggerErrorsEffect = JSON.stringify(errors);

  useEffect(() => {
    if (!isEmpty(errors)) {
      Object.keys(errors).forEach((key) => {
        sendLoyaltyModalEvent('error', `unrecognized|${errors[key].message}`);
      });
    }
  }, [triggerErrorsEffect, errors]);

  const handleNewMember = ({ email }) => {
    claimLoyaltyCouponByEmail(email, toggleLoading);
    sendLoyaltyModalEvent('join now click', 'unrecognized');
  };

  const validateEmail = value => isValidEmail(value) || translate('label.loyalty.coupon.invalidEmail');

  const clearServerError = () => {
    if (Object.keys({ ...error }).length > 0) {
      setError({});
      setFormError('email', {
        type: 'manual',
        message: translate('label.loyalty.coupon.invalidEmail'),
      });
    }
  };

  const siteAbbr = BC.site.code === 'competitivecyclist' ? 'cc' : 'bc';

  return (
    <form onSubmit={handleSubmit(handleNewMember)}>
      {Object.keys(error).length > 0 && (
        <>
          <ErrorBox title={error.title} description={error.description} />
          {!error.title.includes(translate('label.loyalty.coupon.error.server.title')) && <ErrorBox title="" description={translate('label.loyalty.coupon.correctErrors')} /> }
        </>
      )}
      {Object.keys(errors).length > 0 && (
        <ErrorBox title="" description={translate('label.loyalty.coupon.correctErrors')} />
      )}

      <div className={styles.couponInputContainer}>
        <label className={styles.couponInputLabel}>
          {translate('label.loyalty.coupon.email')}
          <span className={styles.requiredPresentation}>
              *
          </span>
        </label>
        <Controller
          render={({ field }) => (
            <input
              className={`${styles.couponInput} ${errors.email && styles.modalInputFieldError}`}
              onChange={(e) => {
                field.onChange(e.target.value);
                setIsEmployee(isEmployeeEmail(e.target.value));
                clearServerError();
              }}
            />
          )}
          control={control}
          rules={{
            required: translate('label.loyalty.coupon.error.invalidEmail'),
            validate: validateEmail,
          }}
          type="email"
          name="email"
          defaultValue=""
        />
        {errors.email && <p className={styles.modalTcError}>{translate('label.loyalty.coupon.error.invalidEmail')}</p>}
      </div>
      <div className={styles.agreeCheckboxWrapper}>
        <Controller
          render={({ field }) => (
            <input
              type="checkbox"
              className={`checkbox-hidden ${styles.checkboxInput}`}
              id="form__agree-checkbox"
              onChange={(e) => {
                field.onChange(e.target.checked);
                if (Object.keys(error).length > 0) {
                  setError({});
                }
              }}
            />
          )}
          control={control}
          rules={{
            required: translate('label.loyalty.coupon.error.agreeToTerms'),
          }}
          name="termsAndCondition"
        />
        <label
          className={`checkbox-label ${
            styles.checkboxLabel
          } ${errors.termsAndCondition && styles.checkboxTickError}`}
          htmlFor="form__agree-checkbox"
        >
          <span className={styles.linkWrapper}>
            {translate('label.loyalty.coupon.agreement')}
            <a className={styles.modalLink} target="_blank" href="/sc/terms-of-use">
              {` ${translate('label.loyalty.coupon.termsOfUse')}, `}
            </a>
            {` ${translate('label.loyalty.coupon.expeditionPerks')} `}
            <a
              className={styles.modalLink}
              id="expedition_perks_link"
              target="_blank"
              rel="noopener noreferrer"
              href={`/sc/${siteAbbr}-expedition-perks-loyalty-program-terms-conditions`}
            >
              {`${translate('label.loyalty.coupon.termsAndCondition')},`}
            </a>
            {` ${translate('label.loyalty.coupon.consent')}`}
            <a className={styles.modalLink} target="_blank" href="/sc/privacy-policy">
              {` ${translate('label.loyalty.coupon.privacyPolicy')}`}
            </a>
            {errors.termsAndCondition && (
              <p className={styles.modalTcError}>{translate('label.loyalty.coupon.error.agreeToTerms')}</p>
            )}
          </span>
        </label>
      </div>

      {isEmployee && (
        <AlertBox
          title={translate('alertbox.title')}
          description={translate('alertbox.description')}
        />
      )}

      <Button
        className={styles.modalJoinBtn}
        disabled={isEmployee}
        type="submit"
      >
        {isSubmitting || isLoading ? (
          <div className="loading-primary" />
        ) : (
          <span>{translate('label.loyalty.coupon.joinNow')}</span>
        )}
      </Button>

      <div className={styles.footerTextWrapper}>
        <p className={styles.footerText}>{translate('label.loyalty.coupon.haveAccountQuestion')}</p>
        <a className={`${styles.modalMutedLink} js-login-modal`} onClick={onExitModal}>{translate('label.loyalty.coupon.signIn')}</a>
      </div>

      <div className={styles.disclaimer}>
        <span className={styles.disclaimerText}>
          {disclaimers.notRecognized}
        </span>
      </div>
    </form>
  );
};

LoyaltyCouponNewUser.propTypes = {
  claimLoyaltyCouponByEmail: PropTypes.func.isRequired,
  error: PropTypes.shape({}).isRequired,
  setError: PropTypes.func.isRequired,
  disclaimers: PropTypes.shape({}).isRequired,
  onExitModal: PropTypes.func.isRequired,
};

export default LoyaltyCouponNewUser;
